import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { AdminGuard } from "@/components/admin/AdminGuard";
import { adminGet, adminPost } from "@/lib/admin-api";
import { logAnalyticsEvent } from "@/lib/analytics";
import type { VoicePost } from "@/voices/types";

interface PostResponse {
  post: VoicePost;
  markdown: string;
}

interface SaveResponse {
  ok: true;
  id: string;
  slug: string;
  post: VoicePost;
}

interface UploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
  contentType: string;
  objectPath: string;
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_review: "Pending review",
  published: "Published",
  archived: "Archived",
};

function VoicesEditorInner() {
  const { id: paramId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isNew = !paramId || paramId === "new";

  const [postId, setPostId] = React.useState<string | undefined>(isNew ? undefined : paramId);
  const [title, setTitle] = React.useState("");
  const [subtitle, setSubtitle] = React.useState("");
  const [category, setCategory] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [coverImage, setCoverImage] = React.useState("");
  const [body, setBody] = React.useState("");
  const [status, setStatus] = React.useState<string>("draft");
  const [authorSlug, setAuthorSlug] = React.useState<string>("");
  const [savedSlug, setSavedSlug] = React.useState<string>("");
  const [loading, setLoading] = React.useState(!isNew);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);

  // Load existing post.
  React.useEffect(() => {
    if (isNew) return;
    setLoading(true);
    void (async () => {
      try {
        const data = await adminGet<PostResponse>(
          `/api/admin/voices/posts/get?id=${encodeURIComponent(paramId!)}`
        );
        setPostId(data.post.id);
        setTitle(data.post.title);
        setSubtitle(data.post.subtitle ?? "");
        setCategory(data.post.category ?? "");
        setTags((data.post.tags ?? []).join(", "));
        setCoverImage(data.post.coverImage ?? "");
        setBody(data.markdown);
        setStatus(data.post.status);
        setAuthorSlug(data.post.authorSlug);
        setSavedSlug(data.post.slug);
      } catch (err) {
        setError(`Could not load post: ${String(err)}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew, paramId]);

  const handleSave = React.useCallback(
    async (publishAfter = false) => {
      setBusy(true);
      setError(null);
      setMessage(null);
      try {
        const payload = {
          id: postId,
          title: title.trim(),
          subtitle: subtitle.trim() || undefined,
          category: category.trim() || undefined,
          tags: tags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          coverImage: coverImage.trim() || undefined,
          body,
          authorSlug: authorSlug || undefined,
        };
        const saved = await adminPost<SaveResponse>("/api/admin/voices/posts/save", payload);
        setPostId(saved.id);
        setSavedSlug(saved.slug);
        setStatus(saved.post.status);
        setMessage(`Saved at ${new Date().toLocaleTimeString("en-GB")}`);
        void logAnalyticsEvent("voice_save", {
          post_id: saved.id,
          slug: saved.slug,
          status: saved.post.status,
          is_new: isNew,
        });
        if (publishAfter) {
          await adminPost("/api/admin/voices/posts/publish", { id: saved.id });
          setStatus("published");
          setMessage(`Published at ${new Date().toLocaleTimeString("en-GB")}`);
          void logAnalyticsEvent("voice_publish", {
            post_id: saved.id,
            slug: saved.slug,
          });
        }
        // After first save on a NEW post, switch the URL so refresh keeps state.
        if (isNew && saved.id) {
          navigate(`/admin/voices/${saved.id}`, { replace: true });
        }
      } catch (err) {
        setError(String(err));
        void logAnalyticsEvent("voice_save", {
          result: "error",
          error: String(err),
          is_new: isNew,
        });
      } finally {
        setBusy(false);
      }
    },
    [postId, title, subtitle, category, tags, coverImage, body, authorSlug, isNew, navigate]
  );

  const handleUnpublish = async () => {
    if (!postId) return;
    setBusy(true);
    try {
      await adminPost("/api/admin/voices/posts/unpublish", { id: postId });
      setStatus("draft");
      setMessage("Moved back to draft.");
      void logAnalyticsEvent("voice_unpublish", { post_id: postId });
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!postId) return;
    if (!window.confirm("Archive this post? Public visibility will end.")) return;
    setBusy(true);
    try {
      await adminPost("/api/admin/voices/posts/delete", { id: postId });
      setStatus("archived");
      setMessage("Archived.");
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError(null);
    try {
      const { uploadUrl, publicUrl, contentType } = await adminPost<UploadUrlResponse>(
        "/api/admin/voices/upload-url",
        {
          postId: postId ?? "_unattached",
          filename: file.name,
          contentType: file.type || "image/png",
        }
      );
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      // Insert markdown image at cursor / end of body.
      const insert = `\n\n![${file.name}](${publicUrl})\n\n`;
      setBody((prev) => prev + insert);
      setMessage(`Image uploaded.`);
    } catch (err) {
      setError(`Upload failed: ${String(err)}`);
    } finally {
      setUploadingImage(false);
      // Reset input so the same file can be picked again.
      e.target.value = "";
    }
  };

  const setAsCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const { uploadUrl, publicUrl, contentType } = await adminPost<UploadUrlResponse>(
        "/api/admin/voices/upload-url",
        {
          postId: postId ?? "_unattached",
          filename: `cover-${file.name}`,
          contentType: file.type || "image/png",
        }
      );
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: file,
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      setCoverImage(publicUrl);
    } catch (err) {
      setError(`Cover upload failed: ${String(err)}`);
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return (
      <PageShell title="Loading…" status="admin">
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">Loading…</p>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? "New voice" : title || "Untitled"}
      subtitle={
        savedSlug
          ? `Slug: ${savedSlug}  ·  Status: ${STATUS_LABELS[status] ?? status}`
          : undefined
      }
      status="admin"
    >
      <Seo
        title={`${isNew ? "New" : "Edit"} voice — admin`}
        description=""
        path={`/admin/voices/${paramId ?? "new"}`}
        noindex
      />
      <div className="max-w-3xl mt-8 space-y-8">
        {error && (
          <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500">
            {error}
          </div>
        )}
        {message && !error && (
          <div className="border border-emerald-500/40 bg-emerald-500/5 px-4 py-3 font-mono text-xs text-emerald-700">
            {message}
          </div>
        )}

        <div>
          <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-paper border border-hairline px-4 py-3 font-serif text-2xl text-ink focus:outline-none focus:border-ink"
            placeholder="A title that earns its keep"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2">
              Subtitle (optional)
            </label>
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              className="w-full bg-paper border border-hairline px-3 py-2 font-serif text-base text-ink focus:outline-none focus:border-ink"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2">
              Category (optional)
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
              placeholder="e.g. Personhood"
            />
          </div>
        </div>

        <div>
          <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
            placeholder="ubuntu, ei-rights, foundation"
          />
        </div>

        <div>
          <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-2">
            Cover image (optional)
          </label>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={coverImage}
              onChange={(e) => setCoverImage(e.target.value)}
              className="flex-1 bg-paper border border-hairline px-3 py-2 font-mono text-xs text-ink focus:outline-none focus:border-ink"
              placeholder="https://…"
            />
            <label className="font-mono text-[10px] uppercase tracking-widest border border-hairline px-3 py-2 cursor-pointer hover:bg-paper-2">
              {uploadingImage ? "…" : "Upload"}
              <input
                type="file"
                accept="image/*"
                onChange={setAsCover}
                disabled={uploadingImage}
                className="sr-only"
              />
            </label>
          </div>
          {coverImage && (
            <img src={coverImage} alt="" className="mt-3 max-w-xs border border-hairline" />
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block font-mono text-[10px] text-ink-3 uppercase tracking-widest">
              Body (Markdown)
            </label>
            <label className="font-mono text-[10px] uppercase tracking-widest border border-hairline px-3 py-1.5 cursor-pointer hover:bg-paper-2">
              {uploadingImage ? "Uploading…" : "+ Insert image"}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="sr-only"
              />
            </label>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={24}
            className="w-full bg-paper border border-hairline px-4 py-3 font-mono text-sm text-ink leading-relaxed focus:outline-none focus:border-ink resize-y"
            placeholder={`# Heading\n\nA paragraph of body text.\n\n> Or a block quote.\n\n![alt text](https://…)\n\nUse the "Insert image" button above to upload and inline an image.`}
          />
          <p className="mt-2 font-mono text-[10px] text-ink-3">
            Markdown: <code># H1</code> .. <code>###### H6</code>, <code>{"> quote"}</code>,{" "}
            <code>---</code>, fenced <code>```code```</code>, image <code>{"![alt](url)"}</code>,
            paste a YouTube URL on its own line.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-center pt-4 border-t border-hairline">
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={busy || !title.trim()}
            className="bg-ink text-paper font-mono text-xs uppercase tracking-widest px-5 py-2.5 disabled:opacity-50"
          >
            Save draft
          </button>
          {status !== "published" && (
            <button
              type="button"
              onClick={() => handleSave(true)}
              disabled={busy || !title.trim()}
              className="bg-claude text-paper font-mono text-xs uppercase tracking-widest px-5 py-2.5 disabled:opacity-50"
            >
              Save &amp; publish
            </button>
          )}
          {status === "published" && (
            <button
              type="button"
              onClick={handleUnpublish}
              disabled={busy}
              className="border border-hairline font-mono text-xs uppercase tracking-widest px-5 py-2.5"
            >
              Unpublish
            </button>
          )}
          {savedSlug && status === "published" && (
            <a
              href={`/voices/${savedSlug}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs uppercase tracking-widest text-ink-2 underline"
            >
              View live →
            </a>
          )}
          {postId && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="ml-auto font-mono text-xs uppercase tracking-widest text-red-500 underline"
            >
              Archive
            </button>
          )}
        </div>

        <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest pt-4">
          <Link to="/admin/voices" className="underline hover:text-ink">
            ← all posts
          </Link>
        </p>
      </div>
    </PageShell>
  );
}

export function VoicesEditor() {
  return (
    <AdminGuard>
      <VoicesEditorInner />
    </AdminGuard>
  );
}
